import { useCallback, useMemo } from 'react';
import QRCode from 'react-qr-code';

import { ReactComponent as OpenLinkIcon } from 'assets/icons/open-link-icon.svg';

import { useAppSelector } from 'store';
import { ResourceLinkType } from 'store/resources';

import { useModal } from 'components/Modal';
import styled from 'styled-components';

type PropsType = {
  resource: ResourceLinkType;
  index: number;
  qrCode: boolean;
  toggleResources: () => void;
};

const Resource: React.FC<PropsType> = ({
  resource,
  index,
  qrCode,
  toggleResources,
}) => {
  const collectionId = useAppSelector(
    (state) => state.game.game.gameInformationFeud?.collection?.id || '',
  );

  const link = useMemo(
    () =>
      qrCode
        ? `${window.location.origin}/collection/${collectionId}`
        : resource.url.startsWith('https://')
        ? resource.url
        : `https://${resource.url}`,
    [resource, collectionId],
  );

  const { Modal: QRCodeModal, openModal } = useModal();

  const handleOpenResource = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (qrCode) {
        e.stopPropagation();
        e.preventDefault();

        openModal();
        toggleResources();
      }
    },
    [qrCode, toggleResources, openModal],
  );

  return (
    <>
      <ResourceWrap>
        <Index>{index + 1}</Index>
        <Link
          href={link}
          target='_blank'
          rel='noreferrer'
          onClick={handleOpenResource}
        >
          <span>{resource.name}</span>
          <OpenLinkIcon />
        </Link>
      </ResourceWrap>
      {qrCode && (
        <QRCodeModal
          style={{
            content: {
              width: 'fit-content',
            },
          }}
          header=''
          type='custom'
          submitOnEnter
        >
          <QRCodeWrap>
            <a href={link} target='_blank' rel='noreferrer'>
              <QRCode value={link} style={{ width: '22rem', height: 'auto' }} />
            </a>
          </QRCodeWrap>
          <QRCodeModalFooter>SCAN OR CLICK THE QR CODE</QRCodeModalFooter>
        </QRCodeModal>
      )}
    </>
  );
};

export default Resource;

const ResourceWrap = styled.li`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 1rem;
  border-radius: 0.75rem;
  background: #1e2d54;
  box-sizing: border-box;
`;

const Index = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  line-height: 1;
  color: #fff;
  border-radius: 50%;
  background: #2d73cc;
  width: 2.5rem;
  height: 2.5rem;
`;

const Link = styled.a`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  font-size: 1.25rem;
  text-decoration-line: underline;
  text-transform: uppercase;

  & svg path {
    transition: fill 0.2 ease-in-out;
    fill: #fff;
  }
`;

const QRCodeWrap = styled.div`
  font-size: 1.5rem;
  text-transform: uppercase;
  border-radius: 0.75rem;
  background: #f2f2f6;
  width: 100%;
  padding: 8.88vh 14.22vh;
  color: #000000;
  text-align: center;
  line-height: normal;
  box-sizing: border-box;
`;

const QRCodeModalFooter = styled.footer`
  font-size: 1.5rem;
  color: #252636;
  text-transform: uppercase;
  border-radius: 0.75rem;
  background: #f2f2f6;
  width: 100%;
  text-align: center;
  padding: 1.1vh 1.33vh;
  box-sizing: border-box;
  margin-top: 1rem;
`;
